import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Inbox, KeyRound, Unplug } from "lucide-react";
import {
  PROVIDER_LABELS,
  sourcesWithoutDestination,
  type ForwardingSettings,
} from "@lucralink/shared";
import { cn } from "@/lib/utils";
import {
  useForwardingHealth,
  type ForwardingChannel,
  type PlatformRoute,
} from "@/lib/hooks/useForwarding";

interface ForwardingHealthProps {
  readonly settings: ForwardingSettings;
  readonly channels: readonly ForwardingChannel[];
  readonly routes: readonly PlatformRoute[];
}

export interface HealthIssues {
  readonly orphanSources: ForwardingChannel[];
  /** Sem destino novo, mas o bot usa o destino legado delas (M8). */
  readonly legacySources: ForwardingChannel[];
  readonly brokenChannels: ForwardingChannel[];
}

/** Origens que nao enviam para ninguem e canais com problema. */
export function computeHealthIssues(
  settings: ForwardingSettings,
  channels: readonly ForwardingChannel[],
  routes: readonly PlatformRoute[],
  legacySourceKeys: readonly string[] = [],
): HealthIssues {
  const legacy = new Set(legacySourceKeys);
  const withoutDestination = (["telegram", "whatsapp"] as const).flatMap((platform) => {
    const sources = channels.filter(
      (c) => c.platform === platform && c.role === "origem" && c.status === "active",
    );
    const destinations = channels
      .filter((c) => c.platform === platform && c.role === "destino" && c.status === "active")
      .map((c) => c.ref);
    const platformRoutes = routes.filter((r) => r.platform === platform);
    const orphanRefs = new Set(
      sourcesWithoutDestination(
        settings.mode,
        sources.map((s) => s.ref),
        destinations,
        platformRoutes,
      ),
    );
    return sources.filter((s) => orphanRefs.has(s.ref));
  });
  const isLegacy = (c: ForwardingChannel): boolean => legacy.has(`${c.platform}:${c.ref}`);
  return {
    orphanSources: withoutDestination.filter((c) => !isLegacy(c)),
    legacySources: withoutDestination.filter(isLegacy),
    brokenChannels: channels.filter((c) => c.status === "error_not_member"),
  };
}

function HealthCard({
  ok,
  icon: Icon,
  title,
  children,
}: {
  ok: boolean;
  icon: typeof Inbox;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <article
      className={cn("rounded-xl border bg-card p-4", ok ? "border-border" : "border-amber-400/30")}
    >
      <header className="flex items-center gap-2">
        <Icon size={16} className={ok ? "text-primary" : "text-amber-300"} aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {ok ? (
          <CheckCircle2 size={14} className="ml-auto text-primary" aria-label="ok" />
        ) : (
          <AlertTriangle size={14} className="ml-auto text-amber-300" aria-label="atenção" />
        )}
      </header>
      <div className="mt-2 text-sm text-muted-foreground">{children}</div>
    </article>
  );
}

function ChannelNames({ items }: { items: readonly ForwardingChannel[] }): JSX.Element {
  return (
    <ul className="mt-1 space-y-0.5">
      {items.slice(0, 6).map((c) => (
        <li key={c.id} className="truncate text-xs text-foreground">
          {c.platform === "telegram" ? "Telegram" : "WhatsApp"}: {c.title ?? c.ref}
        </li>
      ))}
      {items.length > 6 && <li className="text-xs">e mais {items.length - 6}</li>}
    </ul>
  );
}

type HealthQuery = ReturnType<typeof useForwardingHealth>;
type HealthData = NonNullable<HealthQuery["data"]>;

function LegacyNote({ items }: { items: readonly ForwardingChannel[] }): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="mt-2">
      Estas usam o destino da configuração antiga até você definir rotas:
      <ChannelNames items={items} />
    </div>
  );
}

function OrphanSourcesCard({
  mode,
  items,
  legacy,
}: {
  mode: ForwardingSettings["mode"];
  items: readonly ForwardingChannel[];
  legacy: readonly ForwardingChannel[];
}): JSX.Element {
  return (
    <HealthCard ok={items.length === 0} icon={Unplug} title="Origens sem destino">
      <LegacyNote items={legacy} />
      {items.length === 0 ? (
        "Todas as origens ativas enviam para pelo menos um destino."
      ) : (
        <>
          {mode === "advanced"
            ? "No modo avançado, estas origens não têm rota:"
            : "Não há destino ativo para estas origens:"}
          <ChannelNames items={items} />
        </>
      )}
    </HealthCard>
  );
}

function BrokenChannelsCard({ items }: { items: readonly ForwardingChannel[] }): JSX.Element {
  return (
    <HealthCard ok={items.length === 0} icon={AlertTriangle} title="Canais com problema">
      {items.length === 0 ? (
        "Nenhum canal com a conta fora do grupo."
      ) : (
        <>
          A conta conectada saiu destes grupos:
          <ChannelNames items={items} />
        </>
      )}
    </HealthCard>
  );
}

function MissingCredentialsList({
  missing,
}: {
  missing: HealthData["missingCredentialSeen"];
}): JSX.Element {
  return (
    <>
      Ofertas destas lojas chegaram e não foram convertidas por falta de credencial:
      <ul className="mt-1 space-y-0.5">
        {missing.map((m) => (
          <li key={m.marketplace} className="text-xs text-foreground">
            {PROVIDER_LABELS[m.marketplace] ?? m.marketplace}: {m.count} oferta(s)
          </li>
        ))}
      </ul>
      <Link to="/app/account" className="mt-2 inline-block text-xs text-primary hover:underline">
        Cadastrar credenciais
      </Link>
    </>
  );
}

function CredentialsCard({
  isLoading,
  error,
  missing,
}: {
  isLoading: boolean;
  error: HealthQuery["error"];
  missing: HealthData["missingCredentialSeen"];
}): JSX.Element {
  return (
    <HealthCard ok={!error && missing.length === 0} icon={KeyRound} title="Credenciais faltando">
      {isLoading && "Carregando..."}
      {error && "Não foi possível verificar as credenciais agora."}
      {!isLoading &&
        !error &&
        missing.length === 0 &&
        "Nenhuma loja vista nos últimos 7 dias ficou sem credencial."}
      {missing.length > 0 && <MissingCredentialsList missing={missing} />}
    </HealthCard>
  );
}

function QueueCard({
  data,
  error,
}: {
  data: HealthQuery["data"];
  error: HealthQuery["error"];
}): JSX.Element {
  return (
    <HealthCard ok={!error && (data?.failedQueue ?? 0) === 0} icon={Inbox} title="Fila de envio">
      {data
        ? `${data.pendingQueue} oferta(s) aguardando a agenda abrir, ${data.failedQueue} com falha.`
        : error
          ? "Fila indisponível."
          : "Carregando..."}
    </HealthCard>
  );
}

/** Tela de saude do encaminhamento. */
export function ForwardingHealth({
  settings,
  channels,
  routes,
}: ForwardingHealthProps): JSX.Element {
  const { data, isLoading, error } = useForwardingHealth();
  const issues = computeHealthIssues(settings, channels, routes, data?.legacySourceKeys ?? []);
  const validCredentials = new Set(
    (data?.credentials ?? []).filter((c) => c.status === "valid").map((c) => c.marketplace),
  );
  const missing = (data?.missingCredentialSeen ?? []).filter(
    (m) => !validCredentials.has(m.marketplace),
  );

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <OrphanSourcesCard
        mode={settings.mode}
        items={issues.orphanSources}
        legacy={issues.legacySources}
      />
      <BrokenChannelsCard items={issues.brokenChannels} />
      <CredentialsCard isLoading={isLoading} error={error} missing={missing} />
      <QueueCard data={data} error={error} />
    </div>
  );
}
